// i18N
// TYPES
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { THistory, TLogins } from '@timeedit/types/lib/types';
import { Button, Space, Table } from '@timeedit/ui-components/antd';
import intl from '../../../i18n/intl';
import { usersSelector } from '../../../slices/user.slice';
import { getLocalDisplayFormatFromTimestamp } from '../../../utils/timeConverter';

type Props = {
  history: TLogins[] | THistory[];
  type: 'change' | 'logins';
};

type TParsedHistory = {
  modifiedBy: string;
  modified: number;
};

const language = intl.messages;

const renderLoginText = (logins: TLogins[]) =>
  `${language.last_login}: ${
    logins.length
      ? `${getLocalDisplayFormatFromTimestamp(logins[0].login, 'sec')} @ ${
          logins[0].appId
        }`
      : 'User has never logged in'
  }`;

const renderChangeText = (history: TParsedHistory[]) => {
  if (history.length)
    return `${language.modified}: ${getLocalDisplayFormatFromTimestamp(
      history[0].modified,
      'sec'
    )} ${history[0].modifiedBy ? 'by ' + history[0].modifiedBy : ''} `;
  return 'User has never been modified';
};

const changeColumns = [
  {
    title: 'Time',
    dataIndex: 'modified',
    key: 'modified',
    render: (modified: number) =>
      getLocalDisplayFormatFromTimestamp(modified, 'sec'),
  },
  {
    title: 'Modified by',
    dataIndex: 'modifiedBy',
    key: 'modifiedBy',
  },
];

const loginColumns = [
  {
    title: 'Time',
    key: 'login',
    render: (login: any) =>
      getLocalDisplayFormatFromTimestamp(login.login || login.loginTime, 'sec'),
  },
  {
    title: 'App',
    key: 'appId',
    render: (login: any) => login.appId || login.loginType,
  },
];

const UserHistoryAndLogin = ({ history, type }: Props) => {
  const [state, setState] = useState(false);
  const users = useSelector(usersSelector);

  const toggleState = () => {
    setState(!state);
  };

  const parseHistory = (history: THistory[]) => {
    return history.reduce<TParsedHistory[]>(
      (acc: TParsedHistory[], curr: THistory) => {
        const matchedUser = users.find(
          (user) =>
            user.id === curr.modifiedBy || user.legacyId === curr.modifiedBy
        );
        matchedUser
          ? acc.push({
              modifiedBy: matchedUser.username,
              modified: curr.modified,
            })
          : acc.push({
              modifiedBy: 'TimeEdit',
              modified: curr.modified,
            });
        return acc;
      },
      []
    );
  };

  return (
    <div>
      <div className="small-heading-text">
        <Space direction="horizontal">
          <span>
            {type === 'logins' && renderLoginText(history as TLogins[])}
            {type === 'change' &&
              renderChangeText(parseHistory(history as THistory[]))}
          </span>
          <Button
            type="link"
            size="small"
            disabled={!history || !history.length}
            style={{ textDecoration: 'underline' }}
            onClick={toggleState}
          >
            {!state ? 'Show more' : 'Show less'}
          </Button>
        </Space>
      </div>
      {state && (
        <Table
          dataSource={
            type === 'change'
              ? (parseHistory(history as THistory[]) as any[])
              : history
          }
          columns={type === 'change' ? changeColumns : loginColumns}
          size="small"
        />
      )}
    </div>
  );
};

export default UserHistoryAndLogin;
